import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import chatProperties from '../config/chatProperties';
import { getUserName } from '../chatUtils';
import { getUserId } from '../chatUtils';

// Base API configuration
const API_BASE_URL = chatProperties.baseUrl;

// Create API client with default config
const createApiClient = (config?: AxiosRequestConfig): AxiosInstance => {
    const axiosInstance = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 seconds
        ...config,
    });

    // Request interceptor for adding auth token
    axiosInstance.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('token');
            const userId = getUserId();
            const userName = getUserName();
            if (token && config.headers) {
                config.headers['userName'] = userName;
                if (chatProperties.type === 'Admin') {
                    config.headers.Authorization = `Bearer ${token}`;
                    config.headers['Content-Type'] = 'application/json';
                    config.headers['userId'] = userId;
                    config.headers['dAccessType'] = 'false';
                } else {
                    config.headers['authorization'] = '';
                    config.headers['Access-Control-Allow-Origin'] = '*';
                    config.headers['Access-Control-Allow-Headers'] = "Content-Type";
                    config.headers['userName'] = userName;
                    config.headers['apiKey'] = '';
                }
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor for handling errors
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Handle 401 Unauthorized errors (token expired)
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    // Refresh token logic could be implemented here
                    const refreshToken = localStorage.getItem('refresh_token');
                    if (refreshToken) {
                        // Call refresh token endpoint and update tokens
                        // For now, just returning the error
                    }
                } catch (refreshError) {
                    // Handle refresh token failure (e.g., logout user)
                    console.log(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return axiosInstance;
};

// Default API client instance
const apiClient = createApiClient();

// Generic request methods with type support
export const apiRequest = {
    get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        return apiClient.get<T, AxiosResponse<T>>(url, config).then(response => response.data);
    },

    post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        return apiClient.post<T, AxiosResponse<T>>(url, data, config).then(response => response.data);
    },

    put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        return apiClient.put<T, AxiosResponse<T>>(url, data, config).then(response => response.data);
    },

    patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        return apiClient.patch<T, AxiosResponse<T>>(url, data, config).then(response => response.data);
    },

    delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        return apiClient.delete<T, AxiosResponse<T>>(url, config).then(response => response.data);
    },
};

// Allow customization of API clients for different services
export const createCustomApiClient = createApiClient;

export default apiClient; 